import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const scrollToId = (id) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(id, { offset: -72, duration: 1.4 });
  } else {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  }
};

export const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "#missione", label: t.nav.mission, testid: "nav-mission" },
    { id: "#servizi", label: t.nav.services, testid: "nav-services" },
    { id: "#strutture", label: t.nav.structures, testid: "nav-structures" },
    { id: "#lavora-con-noi", label: t.nav.careers, testid: "nav-careers" },
    { id: "#contatti", label: t.nav.contacts, testid: "nav-contacts" },
  ];

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-cream/80 border-b transition-[border-color,box-shadow] duration-300 ${
        scrolled ? "border-line shadow-[0_8px_30px_rgba(45,50,43,0.06)]" : "border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <button
          data-testid="logo-button"
          onClick={() => go("#top")}
          className="font-serif text-3xl font-medium tracking-tight text-ink"
        >
          FILIA<span className="text-terra">.</span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-inksoft hover:text-terra transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
          <div className="flex items-center gap-1 border border-line rounded-full p-1" data-testid="lang-switcher">
            {["it", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-switch-${l}`}
                onClick={() => setLang(l)}
                className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide transition-colors duration-200 ${
                  lang === l ? "bg-terra text-cream" : "text-inksoft hover:text-ink"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </nav>

        <div className="flex md:hidden items-center gap-3">
          <div className="flex items-center gap-1 border border-line rounded-full p-1" data-testid="lang-switcher-mobile">
            {["it", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-switch-mobile-${l}`}
                onClick={() => setLang(l)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${
                  lang === l ? "bg-terra text-cream" : "text-inksoft"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="text-ink p-1"
            aria-label="Menu"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden border-t border-line bg-cream/95 backdrop-blur-xl"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => go(l.id)}
                  className="text-left py-3 text-base font-medium text-ink border-b border-line/60 last:border-0"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
